import type { AssistanceRequest, AssistanceStatus, AssistanceWorkspace, AssistanceQueueRow } from 'types/assistedInstallation';

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
export const assistanceUuid = (value: unknown): value is string => typeof value === 'string'
  && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
export const assistanceDomain = (value: unknown): value is string => typeof value === 'string'
  && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(value);
export const ASSISTANCE_LABELS: Record<AssistanceStatus, string> = {
  pending: 'Pending', awaiting_access: 'Awaiting secure access', call_scheduled: 'Call arranged',
  in_progress: 'In progress', completed: 'Assistance completed', cancelled: 'Cancelled',
};
export const ASSISTANCE_ACTIONS: Record<AssistanceStatus, string> = {
  pending: 'The SHIPAID team will contact you to arrange secure Shopify collaborator access and a call if requested.',
  awaiting_access: 'Follow the team’s secure Shopify collaborator-access request.',
  call_scheduled: 'Follow the call time confirmed separately by the SHIPAID team.',
  in_progress: 'The team is working on this installation request.',
  completed: 'The team marked assistance complete. Verify your published storefront and cart behavior.',
  cancelled: 'This request was cancelled. Submit a new request if you still need assistance.',
};
export const assistanceTransitions: Record<AssistanceStatus, AssistanceStatus[]> = {
  pending: ['awaiting_access', 'in_progress', 'cancelled'], awaiting_access: ['call_scheduled', 'in_progress', 'cancelled'],
  call_scheduled: ['awaiting_access', 'in_progress', 'cancelled'], in_progress: ['awaiting_access', 'completed', 'cancelled'],
  completed: [], cancelled: [],
};
export const assistanceActive = (request: AssistanceRequest | null) => !!request && !['completed', 'cancelled'].includes(request.status);
export function readAssistanceRequest(value: unknown): AssistanceRequest | null {
  if (!record(value) || !assistanceUuid(value.id) || !assistanceUuid(value.storeId) || !assistanceDomain(value.shopDomain)
    || typeof value.callRequested !== 'boolean' || typeof value.status !== 'string'
    || !Object.prototype.hasOwnProperty.call(ASSISTANCE_LABELS, value.status)
    || typeof value.revision !== 'number' || !Number.isInteger(value.revision) || value.revision < 1
    || !['queued', 'dispatching', 'submitted', 'uncertain'].includes(value.notificationStatus as string)
    || typeof value.createdAt !== 'string' || !Number.isFinite(Date.parse(value.createdAt))
    || typeof value.updatedAt !== 'string' || !Number.isFinite(Date.parse(value.updatedAt))) return null;
  return { id: value.id, storeId: value.storeId, shopDomain: value.shopDomain, callRequested: value.callRequested,
    status: value.status as AssistanceRequest['status'], revision: value.revision,
    notificationStatus: value.notificationStatus as AssistanceRequest['notificationStatus'], createdAt: value.createdAt, updatedAt: value.updatedAt };
}
export function readAssistanceWorkspace(value: unknown, storeId: string, domain: string): AssistanceWorkspace | null {
  if (!record(value) || value.schemaVersion !== 1 || value.runtimeStatus !== 'unverified' || value.storeId !== storeId
    || value.shopDomain !== domain || !assistanceUuid(storeId) || !assistanceDomain(domain) || typeof value.available !== 'boolean') return null;
  const request = value.request === null ? null : readAssistanceRequest(value.request);
  if (value.request !== null && (!request || request.storeId !== storeId || request.shopDomain !== domain)) return null;
  return { schemaVersion: 1, storeId, shopDomain: domain, available: value.available, request };
}
export function readAssistanceQueue(value: unknown): AssistanceQueueRow[] | null {
  if (!record(value) || value.schemaVersion !== 1 || !Array.isArray(value.nodes) || value.nodes.length > 50) return null;
  const rows: AssistanceQueueRow[] = [];
  for (const node of value.nodes) {
    const request = readAssistanceRequest(node);
    if (!request || !record(node) || typeof node.contactEmail !== 'string' || node.contactEmail.length > 320
      || !/^[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+$/.test(node.contactEmail) || rows.some(row => row.id === request.id)) return null;
    rows.push({ ...request, contactEmail: node.contactEmail });
  }
  return rows;
}
